import { createSelector } from '@reduxjs/toolkit';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { getHasRequiredCarDocs } from '@/modules/car/store/commonSelectors';
import { getAllCars } from '@/modules/car/store/selectors';
import { getCountryCode } from '@/modules/common/store/selectors';
import { getHasRequiredMyDriverDocs } from '@/modules/driver/store/commonSelectors';
import { checkIsOnBoardingCompleted, getPartner } from '@/modules/partner/store/selectors';
import {
  getIsUserWithPartnerRole,
  selectIsChauffeur,
  selectIsFleet,
  selectIsFleetAndChauffeur,
  selectIsIndividualChauffeur,
  selectIsIndividualChauffeurRU,
  selectIsNew,
  selectIsIndividualChauffeurUK,
} from '@/modules/user/store/selectors';
import { CountryCode } from '@/shared/models/country-code';

import { getIsAppInitialized } from '../store/selectors';

const defaultValue = {
  isReady: false as boolean,
  user: {
    isFleet: false as boolean,
    isChauffeur: false as boolean,
    isFleetAndChauffeur: false as boolean,
    isIndividualChauffeur: false as boolean,
    isIndividualChauffeurRU: false as boolean,
    isBlocked: false as boolean,
    isNew: true as boolean,
  },
  can: {
    notDoAnything: false as boolean,
    viewWizard: false as boolean,
    viewDrivers: false as boolean,
    viewCars: false as boolean,
    createCars: false as boolean,
    viewProfile: false as boolean,
    viewDriverDetails: false as boolean,
    viewTodo: false as boolean,
    viewBills: false as boolean,
    viewJourney: false as boolean,
    viewJourneys: false as boolean,
    viewOverview: false as boolean,
    bookAnAccreditation: false as boolean,
    viewPressure: false as boolean,
  },
} as const;

type TPermissionsCtxValue = typeof defaultValue;

const getPermissions = createSelector(
  getPartner,
  getAllCars,
  getIsAppInitialized,
  checkIsOnBoardingCompleted,
  getHasRequiredCarDocs,
  getHasRequiredMyDriverDocs,
  selectIsFleet,
  selectIsChauffeur,
  selectIsFleetAndChauffeur,
  selectIsIndividualChauffeur,
  selectIsIndividualChauffeurRU,
  selectIsNew,
  getIsUserWithPartnerRole,
  getCountryCode,
  selectIsIndividualChauffeurUK,
  (
    partner,
    cars,
    isAppInitialized,
    isOnboardingCompleted,
    hasRequiredCarDocs,
    hasRequiredDriverDocs,
    isFleet,
    isChauffeur,
    isFleetAndChauffeur,
    isIndividualChauffeur,
    isIndividualChauffeurRU,
    isNew,
    isUserWithPartnerRole,
    region,
    isIndividualChauffeurUK,
  ): TPermissionsCtxValue => {
    if (!isAppInitialized) {
      return defaultValue;
    }

    const isBlocked = partner.accessStatus === 'blocked';

    return {
      isReady: true,
      user: {
        isFleet,
        isChauffeur,
        isFleetAndChauffeur,
        isIndividualChauffeur,
        isIndividualChauffeurRU,
        isNew,
        isBlocked,
      },
      can: {
        notDoAnything: isBlocked,
        viewDrivers:
          isFleetAndChauffeur || isFleet || (isIndividualChauffeurRU && isOnboardingCompleted),
        viewBills:
          isFleetAndChauffeur ||
          isFleet ||
          (isOnboardingCompleted && (isIndividualChauffeur || isIndividualChauffeurRU)),
        viewJourney:
          isFleetAndChauffeur ||
          isFleet ||
          ((isIndividualChauffeurUK || isIndividualChauffeurRU) && isOnboardingCompleted),
        viewJourneys:
          isFleetAndChauffeur ||
          isFleet ||
          ((isIndividualChauffeurUK || isIndividualChauffeurRU) && isOnboardingCompleted),
        viewOverview:
          isFleetAndChauffeur || isFleet || (isIndividualChauffeurRU && isOnboardingCompleted),
        viewCars:
          isFleetAndChauffeur ||
          isFleet ||
          ((isIndividualChauffeur || isIndividualChauffeurRU) && isOnboardingCompleted),
        viewWizard:
          !isOnboardingCompleted && (isNew || isIndividualChauffeur || isIndividualChauffeurRU),
        viewTodo:
          isChauffeur ||
          isFleetAndChauffeur ||
          isFleet ||
          (isOnboardingCompleted && (isIndividualChauffeur || isIndividualChauffeurRU)),
        viewProfile:
          isFleetAndChauffeur ||
          isFleet ||
          (isOnboardingCompleted && (isIndividualChauffeur || isIndividualChauffeurRU)),
        viewDriverDetails: isFleetAndChauffeur || isFleet || isIndividualChauffeurRU,
        createCars:
          isFleetAndChauffeur ||
          isFleet ||
          ((isIndividualChauffeur || isIndividualChauffeurRU) && isOnboardingCompleted),
        bookAnAccreditation: (() => {
          if (isIndividualChauffeur || isIndividualChauffeurRU) {
            switch (region) {
              case CountryCode.GreatBritain:
              case CountryCode.Russia:
                return cars.length > 0 && hasRequiredCarDocs && hasRequiredDriverDocs;
              default:
                return true;
            }
          }

          if (isChauffeur) {
            return true;
          }

          if (isFleet) {
            return true;
          }

          return false;
        })(),
        viewPressure: isUserWithPartnerRole && isOnboardingCompleted,
      },
    };
  },
);

const permissions = React.createContext<TPermissionsCtxValue>(defaultValue);
const { Provider, Consumer: PermissionsConsumer } = permissions;

type Props = {
  children?: React.ReactNode;
};

const PermissionsProvider = ({ children }: Props) => (
  <Provider value={useSelector(getPermissions)}>{children}</Provider>
);

const usePermissionsContext = () => useContext(permissions);

export {
  PermissionsConsumer,
  PermissionsProvider,
  permissions,
  usePermissionsContext,
  getPermissions,
};
export type { TPermissionsCtxValue };
